<template>
  <NavItem
    v-if="isVisible"
    :key="nav.layoutStoreKey"
    :level="level"
    :label="highlightMatch(nav.label, searchTerm)"
    :icon="nav.icon"
    :store-key="nav.layoutStoreKey"
    :to="nav.route"
    :parent-store-key="parentLayoutStoreKey"
    :actions="!isCreateMode && (nav.actions || !!nav.route)"
    :expanded="Boolean(isSearchActive && searchTerm)"
    :expandable="hasChildren"
    :is-create-mode="isCreateMode"
    :disabled="nav.disabled"
    :nav="nav"
    :module-label="moduleLabel"
    :is-mini
    :on-item-click="onItemClick"
    @click="onNavItemClick"
  >
    <template v-if="hasChildren" #default="{ parentStoreKey }">
      <NavTree
        v-for="child in filteredChildren"
        :key="parentStoreKey + child.layoutStoreKey"
        :nav="child"
        :level="level + 1"
        :parent-layout-store-key="parentStoreKey"
        :actions="!isCreateMode && (child.actions || !!child.route)"
        :search-term="searchTerm"
        :is-search-active="isSearchActive"
        :is-create-mode="isCreateMode"
        :module-label="moduleLabel"
        :is-mini
        :on-item-click="onItemClick"
        @close="emit('close', $event)"
      />
    </template>
  </NavItem>
</template>

<script setup lang="ts">
import type { TemplateInfo } from "@scayla/api-client-fetch";
import { highlightMatch } from "@scayla/core";
import { computed } from "vue";

import type { NavNode } from "@/utils/navUtils";
import { nodeMatchesSearch } from "@/utils/navUtils";

import NavItem from "./NavItem.vue";

const props = withDefaults(
  defineProps<{
    nav: NavNode;
    level?: number;
    parentLayoutStoreKey?: string;
    searchTerm?: string;
    isSearchActive?: boolean;
    isCreateMode?: boolean;
    moduleLabel?: string;
    isMini?: boolean;
    onItemClick?: (template: TemplateInfo) => void;
  }>(),
  {
    level: 0,
    parentLayoutStoreKey: undefined,
    searchTerm: "",
    isSearchActive: false,
    isCreateMode: false,
    moduleLabel: "",
    isMini: false,
    onItemClick: undefined,
  },
);

const emit = defineEmits(["close"]);

// Memoized computations with early returns for performance
const hasChildren = computed(() => Boolean(props.nav.children?.length));

// Cache search term processing to avoid repeated toLowerCase calls
const lowerSearchTerm = computed(() => props.searchTerm?.toLowerCase() || "");

const isVisible = computed(() => {
  if (!props.searchTerm) return true;
  return nodeMatchesSearch(props.nav, lowerSearchTerm.value);
});

const filteredChildren = computed(() => {
  if (!props.nav.children) return [];

  // Use the memoized search function for better performance
  const children = !props.searchTerm
    ? props.nav.children
    : props.nav.children.filter((child) => nodeMatchesSearch(child, lowerSearchTerm.value));

  return [...children].sort((a, b) => {
    const aIndex = a.indexId ?? Number.POSITIVE_INFINITY;
    const bIndex = b.indexId ?? Number.POSITIVE_INFINITY;
    return aIndex - bIndex;
  });
});

function onNavItemClick(event: Event) {
  // A folder click only toggles the folder.
  if (props.nav.disabled || !(props.nav.route || props.nav.onItemClick)) return;
  emit("close", event);
}
</script>
